import {
  getClosestNotRemovedTextIndexToLeft,
  getClosestNotRemovedTextIndexToRight,
} from '@src/utility/videoTranscriptionUtils';

import { Element, VideoEditor } from '../editor';
import { KARAOKE_TRACK_NUMBER } from '../editor/constants';
import { parseKaraokeDataFromLegacyTranscriptElements } from '../editor/services/legacyKaraokeImportExport';
import { KaraokeStore } from '../stores-v2/KaraokeStore';
import { resolveCaptionLanguageKey } from '../utility/captionLanguage';
import {
  resolveClipMediaSource,
  transcriptEndSeconds,
} from '../utility/clipMediaSource';
import { extractErrorInfo } from '../utility/error';
import { averageImpactScores } from '../utility/impactScore';
import { LogManager } from '../utility/Logger';
import { extractTranscriptionText } from '../utility/transcriptFormatUtils';
import { deriveReelSections } from './reel/deriveReelSections';
import { reelPlaceholderTitle } from './reel/reelPlaceholderTitle';
import { seedReelFromSubClips } from './reel/seedReelSectionNames';
import { saveVideoResources } from './VideoResourcesProvider';
import { isTempClipId } from './WriteQueueService';

import type { KaraokeData } from '../editor/types/karaoke';
import type { SceneDetection } from '../editor/types/sceneDetection';
import type { VideoSource } from '../stores-v2';
import type {
  ClipMetadata,
  SourcePlatform,
  Story,
  Video,
  VideoAction,
  VideoVersion,
} from '../types.ts/story';
import type { TranscriptElement } from '../types.ts/transcript';
import type { VideoResourceMetadata } from '../types.ts/video';
import type { ClipFragment, TranscriptClipFragment } from './AIClipProducer';
import type { IClipProcessorDeps } from './IClipProcessorDeps';
import type { VideoResourceField } from './VideoResourcesProvider';

export const logger = LogManager.getLogger('AIClipProcessor');

export class AIClipProcessor {
  private clips: ClipFragment[] | TranscriptClipFragment[];
  private deps: IClipProcessorDeps;
  private originalVideo: Video;
  private sourcePlatform: SourcePlatform;
  private sceneDetections: SceneDetection[] | undefined;

  constructor(
    clips: ClipFragment[] | TranscriptClipFragment[],
    deps: IClipProcessorDeps,
    originalVideo: Video,
    sourcePlatform: SourcePlatform,
    sceneDetections?: SceneDetection[],
  ) {
    this.clips = clips;
    this.deps = deps;
    this.originalVideo = originalVideo;
    this.sourcePlatform = sourcePlatform;
    this.sceneDetections = sceneDetections;
    logger.debug('initialized', {
      clips,
      sourcePlatform,
      hasSceneDetections: !!sceneDetections?.length,
    });
  }

  public async saveIndividually(): Promise<Video[]> {
    logger.debug('save:start');

    const results = await Promise.allSettled(
      this.clips.map(async clip => {
        const video = this.newVideo(
          clip.title || `${this.originalVideo.title} - ${clip.theme}`,
        );
        const editor = AIClipProcessor.newVideoEditor(video, this.deps);

        this.cropVideo(editor, [clip]);
        AIClipProcessor.applySceneDetections(editor, this.sceneDetections);

        this.serializeCroppedVideo(
          video,
          editor,
          this.getClipMetadata(clip as ClipFragment),
        );

        logger.debug('save:serialize-done');

        const saved = await this.saveVideo(video);
        logger.debug('save:completed', { videoId: saved.id });
        return saved;
      }),
    );

    const newVideos: Video[] = results
      .filter(
        (r): r is PromiseFulfilledResult<Video> => r.status === 'fulfilled',
      )
      .map(r => r.value);
    logger.info('saveClipsInParallel: done creating videos from clips', {
      total: results.length,
      fulfilled: newVideos.length,
    });
    results.forEach((result, idx) => {
      if (result.status === 'rejected') {
        // Not the clip itself: `relevantElements` can be thousands of words,
        // which Datadog truncates — taking the reason down with it.
        logger.error('saveClipsInParallel: failed to save clip', {
          clipIndex: idx,
          ...extractErrorInfo(result.reason),
          transcriptPosition: this.clips[idx].transcriptPosition && {
            startIndex: this.clips[idx].transcriptPosition.startIndex,
            endIndex: this.clips[idx].transcriptPosition.endIndex,
          },
        });
      }
    });
    return newVideos;
  }

  public async saveAsReel(): Promise<Video> {
    logger.debug('saveAsReel:start');
    const newTitle = this.getReelTitle();

    const video = this.newVideo(newTitle);
    const editor = AIClipProcessor.newVideoEditor(video, this.deps);

    this.cropVideoToReel(editor, this.clips);

    const clipMetadata = this.combineClipsMetadata(this.clips);
    this.serializeCroppedVideo(video, editor, clipMetadata);
    logger.debug('saveAsReel:serialize-done');

    const saved = await this.saveVideo(video);
    logger.debug('save:completed', { videoId: saved.id });
    return saved;
  }

  private newVideo(title: string): Video {
    logger.debug('newVideo:start');
    const video = this.deps.prepareAsCurrentLoadedVideo(
      this.originalVideo,
      title,
      this.sourcePlatform,
    );
    video.lastActionJson = this.videoAction;
    if (this.isByExternalUser) {
      video.isClientReady = true;
    }
    logger.debug('newVideoFromClip:prepared', {
      video,
    });
    return video;
  }

  private get videoAction(): VideoAction {
    return {
      editor: this.deps.currentEditor ?? '',
      date: new Date().getTime(),
      type: 'save',
    };
  }

  private get isByExternalUser(): boolean {
    return !!this.deps.story?.byExternalUser;
  }

  private cropVideo(
    editor: VideoEditor,
    clips: ClipFragment[] | TranscriptClipFragment[],
  ): asserts clips is ClipFragment[] {
    logger.debug('crop:start');
    const mappedClips = clips.map(clip => {
      this.ensureClipData(editor, clip);
      return clip;
    });

    this.cropTimelineToClip(editor, mappedClips);
    logger.debug('crop:timeline-crop-done');
  }

  /**
   * Reel variant of {@link cropVideo}. Uses CropTimelineToReelCommand, which
   * crops the sole video-transcript track to plain video elements and rebuilds
   * the transcript and scenes in one undoable step. Falls back to the legacy
   * crop + scene-detection path when that command's single-track precondition
   * is not met (e.g. the timeline has additional tracks).
   */
  private cropVideoToReel(
    editor: VideoEditor,
    clips: ClipFragment[] | TranscriptClipFragment[],
  ): asserts clips is ClipFragment[] {
    logger.debug('cropReel:start');
    const mappedClips = clips.map(clip => {
      this.ensureClipData(editor, clip);
      return clip;
    });

    const ranges = mappedClips.map(clip => ({
      start: clip.startTime,
      end: clip.startTime + clip.duration,
    }));

    const success = editor.commandCropTimelineToReel(
      ranges,
      this.sceneDetections,
    );
    if (success) {
      logger.debug('cropReel:reel-crop-done', { ranges });
      return;
    }

    logger.debug('cropReel:reel-crop-unavailable, using legacy crop', {
      ranges,
    });
    editor.commandCropTimeline(ranges);
    AIClipProcessor.applySceneDetections(editor, this.sceneDetections);
  }

  private serializeCroppedVideo(
    video: Video,
    editor: VideoEditor,
    clipMetadata: ClipMetadata,
  ) {
    try {
      video.videoSource = editor.timeline.toStoredJSON();
      const transcriptElements =
        editor.getFullTranscript() as TranscriptElement[];
      video.transcriptionSnapshot = { elements: transcriptElements };
      video.transcriptionText = extractTranscriptionText(transcriptElements);
      video.karaokeData = this.serializeKaraokeData(editor);
      video.clipJson = clipMetadata;
      video.scenes = editor.timeline.scenes.map(s => s.toJSON());
      video.extraElementData = editor.getTrackManager().extraElementDataRaw;
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      logger.error('crop:serialize-failed', { error: err.message });
      throw new Error('Failed to serialize cropped video');
    }
  }

  private serializeKaraokeData(editor: VideoEditor): KaraokeData | undefined {
    const store = new KaraokeStore();
    store.syncFromEditor(editor);
    return store.getKaraokeData() ?? undefined;
  }

  private cropTimelineToClip(editor: VideoEditor, clips: ClipFragment[]) {
    try {
      const ranges = clips.map(clip => ({
        start: clip.startTime,
        end: clip.startTime + clip.duration,
      }));
      editor.commandCropTimeline(ranges);
      logger.debug('crop:timeline-crop-done', { ranges });
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      logger.warn('crop:timeline-crop-failed', { error: err.message });
    }
  }

  // Ensure clip has startTime and duration set, try to use transcript if missing.
  // Happens in case of saving clip from transcript
  private ensureClipData(
    editor: VideoEditor,
    clip: ClipFragment | TranscriptClipFragment,
  ): asserts clip is ClipFragment {
    if (typeof clip.startTime !== 'number') {
      const full =
        clip.transcriptPosition.relevantElements ||
        (editor.getFullTranscript() as TranscriptElement[]);
      const elementIndex = getClosestNotRemovedTextIndexToRight(
        clip.transcriptPosition.startIndex,
        full,
      );
      if (elementIndex === -1) {
        throw new Error('Cannot determine clip start time: index not found');
      }
      const element = full[elementIndex];
      if (element.ts == null) {
        throw new Error('Cannot determine clip start time: ts is null');
      }
      clip.startTime = element.ts;
    }
    if (typeof clip.duration !== 'number') {
      const full =
        clip.transcriptPosition.relevantElements ||
        (editor.getFullTranscript() as TranscriptElement[]);
      const elementIndex = getClosestNotRemovedTextIndexToLeft(
        clip.transcriptPosition.endIndex,
        full,
      );
      if (elementIndex === -1) {
        throw new Error('Cannot determine clip duration: index not found');
      }
      const element = full[elementIndex];
      if (element.end_ts == null) {
        throw new Error('Cannot determine clip duration: end_ts is null');
      }
      clip.duration = element.end_ts - clip.startTime;
      if (clip.duration < 0) {
        throw new Error('Invalid clip duration');
      }
    }
  }

  private getReelTitle(): string {
    return reelPlaceholderTitle(this.deps.story!.title);
  }

  private getClipMetadata(clip: ClipFragment): ClipMetadata {
    return {
      duration: clip.duration,
      startTime: clip.startTime,
      muxPlaybackId: clip.muxPlaybackId,
      impactScore: clip.impactScore,
      speakerName: clip.speakerName,
      speakerLabel: clip.speakerLabel,
      theme: clip.theme,
      isReel: false,
      videoTemplateId: clip.videoTemplateId,
      ...(clip.customPrompt && { customPrompt: clip.customPrompt }),
      ...(clip.suggestedTheme && { suggestedTheme: clip.suggestedTheme }),
      ...(clip.transcriptPosition && {
        transcriptPosition: {
          startIndex: clip.transcriptPosition.startIndex,
          endIndex: clip.transcriptPosition.endIndex,
        },
      }),
    };
  }

  private combineClipsMetadata(clips: ClipFragment[]) {
    const averagedImpactScore = averageImpactScores(
      clips.map(c => c.impactScore),
    );
    const customPrompt = clips.find(c => c.customPrompt)?.customPrompt;
    const suggestedTheme = clips.find(c => c.suggestedTheme)?.suggestedTheme;
    return clips.reduce<ClipMetadata>(
      (acc, clip) => {
        acc.duration += clip.duration;
        acc.startTime = Math.min(acc.startTime, clip.startTime);
        return acc;
      },
      {
        isReel: true,
        muxPlaybackId: clips[0].muxPlaybackId,
        duration: 0,
        startTime: Infinity,
        videoTemplateId: clips[0].videoTemplateId,
        theme: clips[0].theme,
        ...(customPrompt && { customPrompt }),
        ...(suggestedTheme && { suggestedTheme }),
        ...(averagedImpactScore && { impactScore: averagedImpactScore }),
        subClips: clips.map(clip => ({
          startTime: clip.startTime,
          duration: clip.duration,
          theme: clip.theme,
          impactScore: clip.impactScore,
          speakerLabel: clip.speakerLabel,
          speakerName: clip.speakerName,
        })),
      },
    );
  }

  public static newVideoEditor(
    video: Video,
    deps: IClipProcessorDeps,
    options?: { skipKaraokeLoad?: boolean },
  ): VideoEditor {
    const editor = new VideoEditor();

    // Enable new transcript architecture for cropping
    editor.setFlag('enableTranscriptMigrationValidation', false);

    // Pass extra element data first — loadVideoSource calls
    // convertMarkedLogosToLogoElements which needs this data.
    editor.passExtraElementData(video.extraElementData ?? {});

    editor.loadVideoSource(video.videoSource);
    AIClipProcessor.hydrateOriginalMediaDuration(editor, deps);
    const transcriptFallback = deps.getTranscriptFallback();
    const ok = editor.loadTranscript(
      video.transcriptionSnapshot?.elements ?? transcriptFallback.final,
      transcriptFallback.original,
      undefined,
      { forcedAlignment: transcriptFallback.forcedAlignment ?? false },
    );
    if (!ok) {
      logger.error('editor:load-transcript-failed');
      throw new Error('Failed to load transcript into temp video editor');
    }

    if (!options?.skipKaraokeLoad) {
      AIClipProcessor.loadKaraokeData(video, deps, editor);
    }
    AIClipProcessor.loadScenes(video, editor);

    return editor;
  }

  /**
   * Applies pre-computed scene detections to the editor after cropping.
   * Creates multiple scenes based on source media scene boundaries.
   */
  private static applySceneDetections(
    editor: VideoEditor,
    sceneDetections: SceneDetection[] | null | undefined,
  ): void {
    if (!sceneDetections || sceneDetections.length === 0) {
      return;
    }
    const success = editor.commandCreateScenesByMediaTime(sceneDetections);
    if (success) {
      logger.info('applySceneDetections: created scenes', {
        sceneCount: sceneDetections.length,
      });
    } else {
      logger.debug('applySceneDetections: scene creation returned false');
    }
  }

  private static loadScenes(video: Video, editor: VideoEditor): void {
    if (video.scenes && video.scenes.length > 0) {
      editor.loadScenes(video.scenes);
    }
  }

  private static loadKaraokeData(
    video: Video,
    deps: IClipProcessorDeps,
    editor: VideoEditor,
  ): void {
    const karaokeContext = deps.getKaraokeContext();
    let karaokeData = karaokeContext?.karaokeData ?? video.karaokeData ?? null;

    if (!karaokeData || !karaokeContext) {
      return;
    }

    const hasKaraokeTrackInSource = (videoSource: VideoSource) => {
      return !!videoSource.elements?.find(
        el => el.track === KARAOKE_TRACK_NUMBER,
      );
    };

    if (
      !karaokeData &&
      hasKaraokeTrackInSource(video.videoSource) &&
      karaokeContext.legacyKaraokeConfigLoaded &&
      karaokeContext.createKaraokeConfig
    ) {
      const migratedConfig = karaokeContext.createKaraokeConfig(
        karaokeContext.legacyKaraokeConfigLoaded,
      );
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      karaokeData = parseKaraokeDataFromLegacyTranscriptElements(
        editor.getFullTranscript(),
        migratedConfig as any,
      );
    }
    if (karaokeData) {
      editor.loadKaraokeData(karaokeData);
    }
  }

  private static hydrateOriginalMediaDuration(
    editor: VideoEditor,
    deps: IClipProcessorDeps,
  ): void {
    try {
      const story = deps.story;
      const fullMediaDuration: number | undefined =
        story?.originalVideo?.video?.duration;
      if (typeof fullMediaDuration !== 'number' || fullMediaDuration <= 0) {
        logger.debug('hydrateMediaDuration:skip:no-duration');
        return;
      }
      for (const track of editor.timeline.tracks) {
        for (const el of track.elements) {
          if (
            !Element.isVideoElement(el) ||
            !deps.isOriginalVideoElement(el.getState()) ||
            el.media_duration != null
          ) {
            continue;
          }
          el.media_duration = fullMediaDuration;
          logger.debug('hydrateMediaDuration:set', {
            elementId: el.id,
            fullMediaDuration,
          });
        }
      }
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      logger.warn('hydrateMediaDuration:error', { error: err.message });
    }
  }

  private async saveVideo(video: Video): Promise<Video> {
    logger.debug('saveVideo:start');
    const story = this.deps.story;
    const videoRepository = this.deps.videoRepository;
    if (!story || !videoRepository) {
      logger.error('saveVideo:missing-deps');
      throw new Error('Cannot save clip: missing story or video repository');
    }

    if (!video.transcriptionSnapshot?.elements) {
      logger.error('saveVideo:no-transcription-snapshot');
      throw new Error(
        'No transcription snapshot present on video, cannot save',
      );
    }

    // PLAT-10788 — AI clips are single-language: a one-entry per-language map
    // keyed by the clip's caption language (DS1/DS5).
    const karaokeByLanguage = video.karaokeData
      ? {
          [resolveCaptionLanguageKey(
            video.karaokeData.configs.get(
              video.karaokeData.defaultConfigId ?? '',
            )?.language,
            this.deps.transcriptLanguage,
            story.originalVideoLanguage,
          )]: video.karaokeData,
        }
      : undefined;

    // Persist videoSource snapshot resource
    const resourcesMetadata = await saveVideoResources(story.id, {
      videoSourceSnapshot: video.videoSource,
      ...(this.deps.flags.enableTranscriptSnapshotResourceUpload && {
        transcriptionSnapshot: video.transcriptionSnapshot,
      }),
      ...(karaokeByLanguage && {
        karaokeData: karaokeByLanguage,
      }),
      ...(video.scenes && {
        scenes: video.scenes,
      }),
    });

    if (
      !resourcesMetadata ||
      Object.keys(resourcesMetadata).some(
        key => !resourcesMetadata[key as VideoResourceField],
      )
    ) {
      logger.error('saveVideo:invalid-resources-metadata', {
        resourcesMetadata,
      });
      throw new Error('Failed to save video resources metadata');
    }
    if (
      this.deps.flags.enableTranscriptSnapshotResourceUpload &&
      !resourcesMetadata.transcriptionSnapshot
    ) {
      throw new Error('Failed to save transcription snapshot metadata');
    }
    if (!resourcesMetadata.videoSourceSnapshot) {
      throw new Error('Failed to save video source snapshot metadata');
    }
    if (video.karaokeData && !resourcesMetadata.karaokeData) {
      logger.warn('Did not save karaoke snapshot metadata');
    }
    if (video.scenes && !resourcesMetadata.scenes) {
      logger.warn('Did not save scenes snapshot metadata');
    }

    const migrationLog = this.deps.getMigrationLog();

    // Build minimal payload for repository
    const payload = {
      ...video,
      resourcesMetadata: {
        videoSourceSnapshot: resourcesMetadata.videoSourceSnapshot,
        ...(resourcesMetadata.transcriptionSnapshot && {
          transcriptionSnapshot: resourcesMetadata.transcriptionSnapshot,
        }),
        ...(resourcesMetadata.karaokeData && {
          karaokeData: resourcesMetadata.karaokeData,
        }),
        ...(resourcesMetadata.scenes && {
          scenes: resourcesMetadata.scenes,
        }),
      },
      ...((!resourcesMetadata.transcriptionSnapshot ||
        this.deps.flags.enableTranscriptSnapshotResourceDualWrite) && {
        transcriptionSnapshot: {
          elements: video.transcriptionSnapshot?.elements || [],
        },
      }),
      ...(resourcesMetadata.transcriptionSnapshot &&
        !this.deps.flags.enableTranscriptSnapshotResourceDualWrite && {
          // TODO: implement before enableTranscriptSnapshotResourceDualWrite should be disabled
          // for migration enableTranscriptSnapshotResourceDualWrite should be enabled, so this block will not be executed
          transcriptionChanges: [],
        }),
      transcriptionMigrationStatus: migrationLog?.status,
      transcriptionMigrationLog: migrationLog?.debugInfo,
      subtitles: undefined, // not generating here
    };

    const { id, slug, hash } = await videoRepository.saveOrUpdateVideo(payload);

    video.id = id;
    video.slug = slug;
    video.hash = hash;
    video._publishedAt = new Date().toISOString();
    logger.debug('saveVideo:success', { id, slug });

    return video;
  }

  /**
   * Build the source Video a lightweight clip expands from. The media URL and
   * the media length are resolved from the story's CURRENT state
   * (resolveClipMediaSource) rather than read off the clip: a clip saved while
   * renditions were still producing points at the editor audio today and at
   * the 270p tomorrow, with no re-save. `clipJson.sourceUrl` is only a last
   * resort for legacy clips whose story can no longer say anything.
   *
   * `media_duration` is set alongside `duration` so the transcript track can
   * be built while the pipeline has not probed the file yet (the story's own
   * duration is null until then) — the transcript end is the usual source.
   */
  public static buildSourceVideoFromStory(
    story: Story,
    clipJson: ClipMetadata,
    opts: {
      transcriptEndSeconds?: number | null;
      localVideoUrl?: string | null;
    } = {},
  ): Video {
    const { sourceStoryId, sourceUrl } = clipJson;
    if (!sourceStoryId) {
      throw new Error('Lightweight clip missing sourceStoryId');
    }

    // Verify we're working with the correct story
    if (story.id !== sourceStoryId) {
      throw new Error(
        `Story mismatch: current story ${story.id} !== source story ${sourceStoryId}`,
      );
    }

    const originalVideo = story.originalVideo;
    if (!originalVideo) {
      throw new Error('Story has no originalVideo');
    }

    const resolved = resolveClipMediaSource(story, opts);
    const source = resolved.url ?? sourceUrl;
    if (!source) {
      throw new Error(
        'Lightweight clip has no playable source yet (no rendition, no editor audio, no baked sourceUrl)',
      );
    }
    logger.debug('buildSourceVideoFromStory:resolved', {
      kind: resolved.url ? resolved.kind : 'baked-sourceUrl',
      renditionPending: resolved.renditionPending,
      durationSeconds: resolved.durationSeconds,
    });

    const durationSeconds = resolved.durationSeconds;
    const videoSource = {
      output_format: 'mp4',
      width: originalVideo.width,
      height: originalVideo.height,
      frame_rate: originalVideo.video?.framerate ?? 30,
      elements: [
        {
          id: crypto.randomUUID(),
          duration: `${durationSeconds ?? 0} s`,
          ...(durationSeconds != null
            ? { media_duration: durationSeconds }
            : {}),
          track: 1,
          time: 0,
          type: 'video',
          source,
          autoplay: true,
          z_index: 1,
        },
      ],
    };

    // Return minimal Video object - transcript comes from transcriptionStore
    return {
      title: story.title,
      videoSource,
      aspectRatio: originalVideo.width > originalVideo.height ? '16:9' : '9:16',
      videoStatus: 'editing',
      associatedVideos: [],
      extraElementData: {},
      sourcePlatform: 'creator-studio',
    } as Video;
  }

  /**
   * Expand a lightweight clip by generating its source representation.
   * This is called lazily when the clip is first loaded for editing.
   */
  public static async expandLightweightClip(
    video: VideoVersion,
    story: Story,
    deps: IClipProcessorDeps,
  ): Promise<void> {
    const clipJson = video.clipJson as ClipMetadata;
    logger.info('expandLightweightClip:start', {
      videoId: video.id,
      sourceStoryId: clipJson.sourceStoryId,
      sourceUrl: clipJson.sourceUrl,
    });

    // Fetch the parent video's full transcript from the story transcript URL.
    // We cannot rely on transcriptionStore.getFinalTranscriptionElements()
    // because it may contain a previously-loaded clip's cropped transcript
    // rather than the parent video's full transcript. Fetched first: while
    // the pipeline has not probed the file, the transcript end is the only
    // media length the source video can carry.
    const transcriptElements =
      await AIClipProcessor.fetchStoryTranscriptElements(story);

    // Build source video from the story's current state (resolve-at-open)
    const sourceVideo = AIClipProcessor.buildSourceVideoFromStory(
      story,
      clipJson,
      { transcriptEndSeconds: transcriptEndSeconds(transcriptElements) },
    );
    if (transcriptElements) {
      sourceVideo.transcriptionSnapshot = { elements: transcriptElements };
    }

    // Create VideoEditor and crop to clip range.
    // Skip karaoke loading from global store — the expansion builds a fresh
    // representation from the parent story and computes karaoke locally below.
    const editor = AIClipProcessor.newVideoEditor(sourceVideo, deps, {
      skipKaraokeLoad: true,
    });

    // Apply scene detections if available (from preloaded story data). Done
    // before the crop so the (reel) scene-boundary snapping has scenes to snap
    // to; the reel crop rebuilds scenes from these detections afterwards.
    AIClipProcessor.applySceneDetections(editor, deps.sceneDetections);

    const subClips = clipJson.isReel ? (clipJson.subClips ?? []) : [];
    const isReel = subClips.length > 0;
    const ranges = isReel
      ? subClips.map(sub => ({
          start: sub.startTime,
          end: sub.startTime + sub.duration,
        }))
      : [
          {
            start: clipJson.startTime,
            end: clipJson.startTime + clipJson.duration,
          },
        ];

    // Reels crop through CropTimelineToReelCommand (plain-video crop + transcript
    // and scene rebuild, input order preserved). It falls back to the legacy crop
    // when its single-transcript-track precondition is not met.
    let expandedClipJson = clipJson;
    if (
      isReel &&
      editor.commandCropTimelineToReel(
        ranges,
        deps.sceneDetections ?? undefined,
      )
    ) {
      logger.debug('expandLightweightClip:reel-crop-done', { ranges });

      // DE8 — the crop has just minted the Section ids, so this is the first
      // moment the titles the producer stored on `subClips` have keys to be
      // stored under. Without it a reel's rows open named after their opening
      // words even though every clip that went into it had a name.
      const reel = seedReelFromSubClips(
        deriveReelSections(editor.timeline),
        subClips,
        clipJson.reel,
      );
      if (reel) {
        expandedClipJson = { ...clipJson, reel };
        logger.debug('expandLightweightClip:reel-names-seeded', {
          named: Object.keys(reel.sections).length,
        });
      }
    } else {
      editor.commandCropTimeline(ranges);
    }

    // Serialize cropped data onto the video
    video.videoSource = editor.timeline.toStoredJSON();
    video.transcriptionSnapshot = {
      elements: editor.getFullTranscript() as TranscriptElement[],
    };

    // Serialize karaoke data if present (local instance to avoid corrupting global state)
    const karaokeStore = new KaraokeStore();
    karaokeStore.syncFromEditor(editor);
    const rawKaraokeData = karaokeStore.getKaraokeData();
    video.karaokeData = rawKaraokeData
      ? structuredClone(rawKaraokeData)
      : undefined;

    // Serialize scenes
    video.scenes = editor.timeline.scenes.map(s => s.toJSON());

    // The seeded names travel with the clip whether or not the persist below
    // runs: a reel still holding a temp id is skipped there, and is picked up
    // again by `persistExpandedResourcesForClipPreview` once it has a real one.
    video.clipJson = expandedClipJson;

    // Persist expanded resources to S3 for future loads
    await AIClipProcessor.persistExpandedClipResources(
      video,
      expandedClipJson,
      story,
      deps,
    );

    logger.info('expandLightweightClip:complete', { videoId: video.id });
  }

  /**
   * Fetch transcript elements directly from the story's transcript URL.
   * Returns the full parent video transcript independent of any store state.
   */
  private static async fetchStoryTranscriptElements(
    story: Story,
  ): Promise<TranscriptElement[] | null> {
    const transcriptUrl = story.transcription?.elementsJson?.url;
    if (!transcriptUrl) {
      logger.warn('fetchStoryTranscriptElements:no-url');
      return null;
    }
    try {
      const response = await fetch(transcriptUrl);
      if (!response.ok) {
        logger.warn('fetchStoryTranscriptElements:fetch-failed', {
          status: response.status,
        });
        return null;
      }
      const data: { elements?: TranscriptElement[] } = await response.json();
      if (!data.elements || data.elements.length === 0) {
        logger.warn('fetchStoryTranscriptElements:no-elements');
        return null;
      }
      logger.debug('fetchStoryTranscriptElements:success', {
        elementCount: data.elements.length,
      });
      return data.elements;
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      logger.warn('fetchStoryTranscriptElements:error', {
        error: err.message,
      });
      return null;
    }
  }

  /**
   * Persist expanded clip resources to S3 so subsequent loads are fast.
   * Public so it can be called when a temp clip gets its real ID.
   */
  public static async persistExpandedClipResources(
    video: VideoVersion,
    clipJson: ClipMetadata,
    story: Story,
    deps: IClipProcessorDeps,
  ): Promise<void> {
    // Skip persistence for temp clips - will persist when real ID is available
    if (isTempClipId(video.id)) {
      logger.info('persistExpandedClipResources: skipping for temp clip', {
        videoId: video.id,
      });
      return;
    }

    try {
      // PLAT-10788 — one-entry per-language karaoke map for the expanded clip.
      const karaokeByLanguage = video.karaokeData
        ? {
            [resolveCaptionLanguageKey(
              video.karaokeData.configs.get(
                video.karaokeData.defaultConfigId ?? '',
              )?.language,
              deps.transcriptLanguage,
              story.originalVideoLanguage,
            )]: video.karaokeData,
          }
        : undefined;

      // Upload expanded resources to S3
      const resourcesMetadata = await saveVideoResources(story.id, {
        videoSourceSnapshot: video.videoSource,
        transcriptionSnapshot: video.transcriptionSnapshot,
        ...(karaokeByLanguage && { karaokeData: karaokeByLanguage }),
        ...(video.scenes && { scenes: video.scenes }),
      });

      // Update database with resources and mark as no longer lightweight
      const updatedClipJson: ClipMetadata = {
        ...clipJson,
        isLightweightClip: false,
      };

      if (!video.id) {
        throw new Error('Video ID is required for update');
      }

      // Filter out null values from resourcesMetadata
      const filteredResourcesMetadata = Object.fromEntries(
        Object.entries(resourcesMetadata).filter(([, v]) => v != null),
      ) as Partial<Record<VideoResourceField, VideoResourceMetadata>>;

      await deps.videoRepository?.updateVideo?.({
        id: video.id,
        resourcesMetadata: filteredResourcesMetadata,
        clipJson: updatedClipJson,
      });

      video.resourcesMetadata = filteredResourcesMetadata;
      video.clipJson = updatedClipJson;

      logger.debug('persistExpandedClipResources:success', {
        videoId: video.id,
      });
    } catch (err) {
      // Log but don't fail - clip will work, just won't be cached
      const error = err instanceof Error ? err : new Error(String(err));
      logger.warn('persistExpandedClipResources:failed', {
        videoId: video.id,
        error: error.message,
      });
    }
  }
}
